import type { ExpenseCategory } from '@projections/engine';
import { useFieldArray } from 'react-hook-form';
import { Field, FieldRow } from '../../../forms/fields';
import { GrowthField } from '../fields/GrowthField';
import { TimePointField } from '../fields/TimePointField';
import type { SectionProps } from '../sectionProps';

/**
 * EXPENSES section — a `useFieldArray` over `expenses`. Mirrors IncomeSection (name, amount, the
 * shared GrowthField, start/end TimePoints) with one extra control: the `category` select that
 * groups the ledger drill-down (living | housing | education | healthcare | discretionary | other).
 */

const EXPENSE_CATEGORIES: ReadonlyArray<{ value: ExpenseCategory; label: string }> = [
  { value: 'living', label: 'Living' },
  { value: 'housing', label: 'Housing' },
  { value: 'education', label: 'Education' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'discretionary', label: 'Discretionary' },
  { value: 'other', label: 'Other' },
];

export function ExpensesSection({ form }: SectionProps): React.ReactElement {
  const { register, control, formState } = form;
  const { fields, append, remove } = useFieldArray({ control, name: 'expenses' });
  const errors = formState.errors.expenses;
  const eventsError = formState.errors.events;

  return (
    <section>
      <h2>Expenses</h2>
      <p style={{ color: 'var(--ink-muted)', marginBottom: 'var(--sp-6)' }}>
        Outflows by category. CPI-indexed expenses hold their purchasing power against the simulated
        year's realized inflation.
      </p>

      {typeof eventsError?.message === 'string' && (
        <p className="field__error" style={{ marginBottom: 'var(--sp-4)' }}>
          {eventsError.message}
        </p>
      )}

      {fields.map((f, i) => {
        const rowErrors = errors?.[i];
        return (
          <div key={f.id} className="card" style={{ marginBottom: 'var(--sp-4)' }}>
            <FieldRow>
              <div style={{ flex: 2, minWidth: 160 }}>
                <Field label="Name" error={rowErrors?.name} htmlFor={`expenses.${i}.name`}>
                  <input
                    id={`expenses.${i}.name`}
                    className="input"
                    {...register(`expenses.${i}.name`)}
                  />
                </Field>
              </div>
              <div style={{ flex: 1, minWidth: 140 }}>
                <Field
                  label="Amount ฿ / yr"
                  error={rowErrors?.amount}
                  htmlFor={`expenses.${i}.amount`}
                >
                  <input
                    id={`expenses.${i}.amount`}
                    className="input input--num"
                    type="number"
                    {...register(`expenses.${i}.amount`, { valueAsNumber: true })}
                  />
                </Field>
              </div>
              <div style={{ flex: 1, minWidth: 150 }}>
                <Field
                  label="Category"
                  error={rowErrors?.category}
                  htmlFor={`expenses.${i}.category`}
                >
                  <select
                    id={`expenses.${i}.category`}
                    className="select"
                    {...register(`expenses.${i}.category`)}
                  >
                    {EXPENSE_CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </FieldRow>

            <GrowthField
              form={form}
              name={`expenses.${i}.growthMode`}
              error={rowErrors?.growthMode}
            />

            <FieldRow>
              <div style={{ flex: 1, minWidth: 220 }}>
                <TimePointField
                  form={form}
                  name={`expenses.${i}.start`}
                  label="Start"
                  error={rowErrors?.start}
                />
              </div>
              <div style={{ flex: 1, minWidth: 220 }}>
                <TimePointField
                  form={form}
                  name={`expenses.${i}.end`}
                  label="End"
                  optional
                  error={rowErrors?.end}
                />
              </div>
            </FieldRow>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--sp-3)' }}>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => remove(i)}
                aria-label={`Remove expense ${i + 1}`}
              >
                Remove expense
              </button>
            </div>
          </div>
        );
      })}

      <button
        type="button"
        className="btn"
        onClick={() =>
          append({
            id: `expense${fields.length + 1}`,
            name: '',
            amount: 0,
            growth: 0,
            growthMode: { kind: 'fixed', rate: 0 },
            start: { kind: 'year', year: form.getValues('startYear') },
            category: 'living',
          })
        }
      >
        + Add expense
      </button>
    </section>
  );
}
